import { useQuery } from "@tanstack/react-query"
import { apiClient } from "@/react-query/api-client"
import { API_ROUTES } from "@/react-query/api-routes"
import type { ApiEnvelope } from "@/react-query/api-types"
import { CACHE_TIME } from "@/react-query/cache-times"
import { queryKeys } from "@/react-query/query-keys"

export const FEATURE_FLAG_KEYS = {
  NEWS: "NEWS_FEATURE",
  CHALLENGES: "CHALLENGES_FEATURE",
} as const

export type FeatureFlag = {
  id: number
  name: string
  value: string
  createdBy: string
  createdOn: string
  updatedBy: string | null
  updatedOn: string | null
}

export function isFeatureEnabled(value?: string) {
  return value?.toUpperCase() === "TRUE"
}

export async function fetchFeatureFlags(): Promise<FeatureFlag[]> {
  const response = await apiClient<ApiEnvelope<FeatureFlag[]>>(
    API_ROUTES.featureFlagsAll
  )
  return response.data ?? []
}

export function useFeatureFlagsQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.featureFlags(),
    queryFn: fetchFeatureFlags,
    enabled,
    // Flags only change on deploy; keep them cached for the whole session.
    staleTime: CACHE_TIME.INFINITY,
    gcTime: CACHE_TIME.INFINITY,
    refetchOnWindowFocus: false,
  })
}

export function useFeatureFlagQuery(name: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.featureFlags(),
    queryFn: fetchFeatureFlags,
    enabled: enabled && Boolean(name),
    staleTime: CACHE_TIME.INFINITY,
    gcTime: CACHE_TIME.INFINITY,
    refetchOnWindowFocus: false,
    select: (flags) => flags.find((flag) => flag.name === name),
  })
}
